<!-- 翻车机作业计划 -->
<template>
  <div>
    <avue-crud
      ref="crud"
      v-model="form"
      :option="option"
      :table-loading="loading"
      :data="data"
      :page.sync="page"
      :search.sync="query"
      :defaults.sync="defaults"
      @search-change="searchChange"
      @search-reset="searchReset"
      @refresh-change="refreshChange"
      @current-change="currentChange"
      @size-change="sizeChange"
      @on-load="onLoad"
    >
      <template slot="menuLeft">
        <el-button
          type="default"
          size="small"
          plain
          icon="el-icon-download"
          :loading="exportLoading"
          @click="handleExport"
        >导出</el-button>
      </template>
    </avue-crud>
  </div>
</template>

<script>
import crudMixins from "@/mixins/crud-mixins";
import { getList, getDetail, exportExcel } from "./api";
import { searchTimeConfig } from "@/basic-common/util/common";
import { downloadXls } from "@/util/util";

export default {
  mixins: [crudMixins],
  data() {
    return {
      apiConfig: { getList, getDetail },
      defaultParams: {},
      exportLoading: false,
      infoCardData: [], // 卡片数据
      option: {
        ...this.$store.state.publicOption,
        menu: true,
        addBtn: false,
        editBtn: false,
        delBtn: false,
        viewBtn: true,
        column: [
          // 新增字段
          {
            label: '计划号',
            prop: 'planNo',
            minWidth: 160,
            search: true
          },
          {
            label: '车号',
            prop: 'trainNumber',
            minWidth: 140,
            search: true
          },
          // 原有字段（保留）
          {
            label: '计划日期',
            prop: 'planDate',
            type: 'date',
            search: true,
            format: 'yyyy-MM-dd',
            valueFormat: 'yyyy-MM-dd',
            rules: [
              { required: true, message: '请选择计划日期', trigger: 'blur' }
            ]
          },
          {
            label: '计划翻卸车数',
            prop: 'plannedVehicles',
            type: 'number',
            search: false,
            rules: [
              { required: true, message: '请输入计划翻卸车数', trigger: 'blur' },
              { type: 'integer', message: '请输入整数', trigger: 'blur' },
              {
                validator: (rule, value, callback) => {
                  if (value !== null && value !== undefined && value <= 0) {
                    callback(new Error('计划翻卸车数必须大于0'));
                  } else {
                    callback();
                  }
                },
                trigger: 'blur'
              }
            ],
            precision: 0,
            step: 1,
            change: this.calculateAutoFields
          },
          {
            label: '实际翻卸车数',
            prop: 'actualVehicles',
            type: 'number',
            search: false,
            rules: [
              { required: true, message: '请输入实际翻卸车数', trigger: 'blur' },
              { type: 'integer', message: '请输入整数', trigger: 'blur' },
              {
                validator: (rule, value, callback) => {
                  if (value !== null && value !== undefined && value < 0) {
                    callback(new Error('实际翻卸车数不能为负数'));
                  } else {
                    callback();
                  }
                },
                trigger: 'blur'
              }
            ],
            precision: 0,
            change: this.calculateAutoFields
          },
          {
            label: '滞后量',
            prop: 'lagCount',
            type: 'number',
            search: false,
            precision: 0,
            disabled: true,
            minWidth: 100
          },
          {
            label: '实际作业时长(分钟)',
            prop: 'actualHours',
            type: 'number',
            search: false,
            rules: [
              { required: true, message: '请输入实际作业时长', trigger: 'blur' }
            ],
            precision: 2,
            step: 1
          },
          {
            label: '完成率(%)',
            prop: 'completionRate',
            type: 'number',
            search: false,
            rules: [
              { type: 'number', message: '请输入数字', trigger: 'blur' }
            ],
            precision: 2,
            step: 0.01,
            disabled: true
          },
          {
            label: '备注',
            prop: 'remark',
            maxlength: 50,
            type: "textarea",
            span: 24,
            minRows: 3,
            hide: true,
            search: false,
            rules: [
              { max: 50, message: '备注不能超过50个字符', trigger: 'blur' }
            ]
          },
          // 新增字段：开始/结束时间 + 计划状态
          {
            label: '开始作业时间',
            prop: 'startTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 180
          },
          {
            label: '结束作业时间',
            prop: 'endTime',
            type: 'datetime',
            format: 'yyyy-MM-dd HH:mm:ss',
            valueFormat: 'yyyy-MM-dd HH:mm:ss',
            minWidth: 180
          },
          {
            label: '计划状态',
            prop: 'railPlanPhase',
            type: 'select',
            dataType: 'number',
            dicData: [
              { label: '待煤流线确认', value: 0 },
              { label: '作业中', value: 1 },
              { label: '作业完成', value: 2 }
            ],
            minWidth: 140
          },
          {
            ...searchTimeConfig,
            paramsKey: ["startTime", "endTime"],
            search: false
          }
        ]
      },
    }
  },
  methods: {
    async handleExport() {
      const total = this.page.total || 0;
      if (total < 1) {
        this.$message.warning('无数据可导出');
        return;
      }
      const maxRows = 10000;
      if (total > maxRows) {
        this.$message.warning(`当前最多导出 ${maxRows} 条，请缩小筛选范围后重试`);
        return;
      }
      this.exportLoading = true;
      try {
        const params = this.formatParams(Object.assign({}, this.defaultParams, this.query));
        const res = await exportExcel(params);
        downloadXls(res.data, `翻车机作业计划_${Date.now()}.xlsx`);
        this.$message.success('导出成功');
      } catch (e) {
        console.warn(e);
        this.$message.error((e && e.message) || '导出失败');
      } finally {
        this.exportLoading = false;
      }
    },
    /**
     * 计算自动填充字段（完成率）
     */
    calculateAutoFields() {
      if (
        this.form.plannedVehicles !== null &&
        this.form.plannedVehicles !== undefined &&
        this.form.actualVehicles !== null &&
        this.form.actualVehicles !== undefined
      ) {
        if (this.form.plannedVehicles > 0) {
          const rate = (this.form.actualVehicles / this.form.plannedVehicles) * 100;
          this.form.completionRate = Math.round(rate * 100) / 100;
        } else {
          this.form.completionRate = 0;
        }
      } else {
        this.form.completionRate = 0;
      }
    }
  },
  watch: {
    'form.plannedVehicles': {
      handler() {
        this.calculateAutoFields();
      },
      immediate: true
    },
    'form.actualVehicles': {
      handler() {
        this.calculateAutoFields();
      },
      immediate: true
    }
  }
}
</script>
<style scoped lang="scss">
</style>
